import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Bed, Note, Photo, Plant, Zone } from '../types';

// ── Read ────────────────────────────────────────────────────────────────────

export function usePropertyMap() {
  return useQuery({ queryKey: ['map'], queryFn: api.getMap });
}

export function useZones() {
  return useQuery({ queryKey: ['zones'], queryFn: api.getZones });
}

export function useBeds() {
  return useQuery({ queryKey: ['beds'], queryFn: () => api.getBeds() });
}

export function usePlants(params?: Parameters<typeof api.getPlants>[0]) {
  return useQuery({
    queryKey: ['plants', params],
    queryFn: () => api.getPlants(params),
  });
}

export function usePhotos(entityType: 'plant' | 'bed', entityId: string | null) {
  return useQuery({
    queryKey: ['photos', entityType, entityId],
    queryFn: () => api.getPhotos(entityType, entityId!),
    enabled: !!entityId,
  });
}

export function useNotes(entityType: 'plant' | 'bed', entityId: string | null) {
  return useQuery({
    queryKey: ['notes', entityType, entityId],
    queryFn: () => api.getNotes(entityType, entityId!),
    enabled: !!entityId,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

export function useZoneMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['zones'] });
  return {
    create: useMutation({ mutationFn: (d: Partial<Zone>) => api.createZone(d), onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Zone> }) => api.updateZone(id, data),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteZone, onSuccess: inv }),
  };
}

export function useBedMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['beds'] });
  return {
    create: useMutation({ mutationFn: (d: Partial<Bed>) => api.createBed(d), onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Bed> }) => api.updateBed(id, data),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteBed, onSuccess: inv }),
  };
}

export function usePlantMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['plants'] });

  const move = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) =>
      api.updatePlant(id, { x, y } as Partial<Plant>),
    // Optimistic update so dragging feels instant
    onMutate: async ({ id, x, y }) => {
      await qc.cancelQueries({ queryKey: ['plants'] });
      const prev = qc.getQueryData<Plant[]>(['plants']);
      qc.setQueriesData<Plant[]>({ queryKey: ['plants'] }, (old) =>
        old?.map((p) => (p.id === id ? { ...p, x, y } : p)) ?? [],
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['plants'], ctx.prev);
    },
    onSettled: inv,
  });

  return {
    create: useMutation({ mutationFn: (d: Partial<Plant>) => api.createPlant(d), onSuccess: inv }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Plant> }) =>
        api.updatePlant(id, data),
      onSuccess: inv,
    }),
    move,
    remove: useMutation({ mutationFn: api.deletePlant, onSuccess: inv }),
  };
}

export function usePhotoMutations(entityType: 'plant' | 'bed', entityId: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['photos', entityType, entityId] });
  return {
    upload: useMutation({
      mutationFn: ({ file, caption, dateTaken }: { file: File; caption?: string; dateTaken?: string }) =>
        api.uploadPhoto(entityType, entityId, file, caption, dateTaken),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deletePhoto(id),
      onSuccess: inv,
    }),
  };
}

export function useNoteMutations(entityType: 'plant' | 'bed', entityId: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ['notes', entityType, entityId] });
  return {
    create: useMutation({
      mutationFn: (d: Partial<Note>) => api.createNote({ ...d, entity_type: entityType, entity_id: entityId }),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Note> }) => api.updateNote(id, data),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: api.deleteNote, onSuccess: inv }),
  };
}
